import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class ErrorPage extends i18nMixin(NeptuneElement) {

	static ERROR_MESSAGES_URL = '/src/components/chrome/assets/errorMessage.json';
	static DEFAULT_HOME_PAGE_URL = '/';

	/** @type {Map<string, Promise<object>>} Shared fetch-promise cache for error messages JSON, keyed by URL. */
	static #errorMessagesCache = (window.__neptuneErrorMessagesCache ??= new Map());

	templateMarkup = `
		<section>
			<h1></h1>
			<p></p>
			<div>
				<neptune-button type="primary" size="default" icon-start="fa-arrow-left"> Back to previous page </neptune-button>
				<neptune-button type="secondary" size="default"> Return to home page </neptune-button>
			</div>
		</section>
	`;

	componentCssUrl = '/src/components/chrome/errorPage/error-page.css';

	errorMessages = {};

	async connectedCallback() {
		await super.connectedCallback();
		const culture = this.getAttribute('culture');
		if (culture) { i18n.setLocale(culture); }
		await this.loadErrorMessages();
        this.processInitialAttributesAfterRender(ErrorPage.observedAttributes);
		this.#applyTranslations();
	}
	/**
	 * @description Called when the locale changes. This method is part of the i18nMixin and is triggered whenever the active locale is updated.
	 * It ensures that the error page content is re-rendered with the appropriate translations for the new locale.
	 * This method is automatically called by the i18nMixin when the locale changes, so you don't need to call it manually.
	 * It will update the error heading, message, and button labels based on the current locale.
	 * If the error code has a specific translation in the new locale, it will be used; otherwise, it will fall back to the default messages.
	 * This allows for dynamic localization of the error page without requiring a full page reload.
	 * Note: Ensure that the translations for the error codes are available in the locale JSON files for proper rendering.
	 * If a translation is missing, it will fall back to the default English messages or the messages defined in the errorMessage.json file.
	 */
	onLocaleChange() {
		this.#applyTranslations();
	}

	static get observedAttributes() {
		return ['error-code', 'home-page-url', 'culture'];
	}

	setEventListeners() {
		this.backButton = this.shadowRoot?.querySelector('section > div > neptune-button:first-of-type');
		this.homeButton = this.shadowRoot?.querySelector('section > div > neptune-button:last-of-type');

		this.handleBackClickBound = this.handleBackClick.bind(this);
		this.handleHomeClickBound = this.handleHomeClick.bind(this);

		this.backButton?.addEventListener('click', this.handleBackClickBound);
		this.homeButton?.addEventListener('click', this.handleHomeClickBound);
	}

	removeEventListeners() {
		this.backButton?.removeEventListener('click', this.handleBackClickBound);
		this.homeButton?.removeEventListener('click', this.handleHomeClickBound);

		this.backButton = null;
		this.homeButton = null;
		this.handleBackClickBound = null;
		this.handleHomeClickBound = null;
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (!this.isRendered || oldValue === newValue) {
			return;
		}

		this.processAttribute(name, oldValue, newValue);
	}

	processAttribute(name, oldValue, newValue) {
		if (name === 'culture' && newValue) {
			i18n.setLocale(newValue);
			this.#applyTranslations();
		}
		if (name === 'error-code') { this.#applyTranslations(); }
	}

	getHomePageUrl() {
		const homePageUrl = (this.getAttribute('home-page-url') || '').trim();
		return homePageUrl || ErrorPage.DEFAULT_HOME_PAGE_URL;
	}

	async loadErrorMessages() {
		const url = ErrorPage.ERROR_MESSAGES_URL;
		if (!ErrorPage.#errorMessagesCache.has(url)) {
			const messagesPromise = fetch(url)
				.then(async response => {
					if (!response.ok) {return {};}
					const parsed = await response.json();
					return parsed ?? {};
				})
				.catch(() => ({}));
			ErrorPage.#errorMessagesCache.set(url, messagesPromise);
		}
		this.errorMessages = await ErrorPage.#errorMessagesCache.get(url);
	}

	/**
	 * @description Applies translations to the error page content based on the current locale and error code.
	 * It updates the error heading, message, and button labels with the appropriate translations.
	 * If a translation for the specific error code is not available, it falls back to the default messages defined in the errorMessages object.
	 * This method is called whenever the locale changes or when the error code attribute is updated.
	 * It ensures that the error page content is always displayed in the correct language and format for the user.
	 * Note: Ensure that the translations for the error codes are available in the locale JSON files for proper rendering.
	 * If a translation is missing, it will fall back to the default English messages or the messages defined in the errorMessage.json file.
	 * This allows for dynamic localization of the error page without requiring a full page reload.
	 * @returns {void}
	 */
	#applyTranslations() {
		const errorHeading = this.shadowRoot?.querySelector('section > h1');
		const errorMessage = this.shadowRoot?.querySelector('section > p');
		const backButton = this.shadowRoot?.querySelector('section > div > neptune-button:first-of-type');
		const homeButton = this.shadowRoot?.querySelector('section > div > neptune-button:last-of-type');

		if (!errorHeading || !errorMessage) { return; }

		const errorCode = this.getAttribute('error-code') || '404';

		const i18nTitle = this.translate(`ERROR_PAGE.ERRORS.${errorCode}.TITLE`);
		const i18nMessage = this.translate(`ERROR_PAGE.ERRORS.${errorCode}.MESSAGE`);

		const fallbackConfig = this.errorMessages[errorCode] || this.errorMessages['404'];
		const title = (i18nTitle !== `ERROR_PAGE.ERRORS.${errorCode}.TITLE`) ? i18nTitle : (fallbackConfig?.TITLE || fallbackConfig?.title || 'Unknown Error');
		const message = this.getAttribute('error-message')
			|| ((i18nMessage !== `ERROR_PAGE.ERRORS.${errorCode}.MESSAGE`) ? i18nMessage : (fallbackConfig?.MESSAGE || fallbackConfig?.message || 'Something went wrong while loading this page.'));

		errorHeading.textContent = `${title} (${errorCode})`;
		errorMessage.textContent = message;

		if (backButton) { backButton.textContent = this.translate('ERROR_PAGE.BUTTON.BACK'); }
		if (homeButton) { homeButton.textContent = this.translate('ERROR_PAGE.BUTTON.HOME'); }
	}

	/**
	 * @description Updates the error page content based on the current locale and error code.
	 * This method is called whenever the locale changes or when the error code attribute is updated.
	 * It ensures that the error page content is always displayed in the correct language and format for the user.
	 * Note: Ensure that the translations for the error codes are available in the locale JSON files for proper rendering.
	 * If a translation is missing, it will fall back to the default English messages or the messages defined in the errorMessage.json file.
	 * This allows for dynamic localization of the error page without requiring a full page reload.
	 * @returns {void}
	 */
	updateErrorContent() {
		this.#applyTranslations();
	}

	handleBackClick() {
		if (window.history.length > 1) {
			window.history.back();
			return;
		}

		this.navigateTo(this.getHomePageUrl());
	}

	handleHomeClick() {
		this.navigateTo(this.getHomePageUrl());
	}

	navigateTo(url) {
		window.location.assign(url);
	}
}

customElements.define('neptune-error-page', ErrorPage);
export default ErrorPage;